export const generalFaqs = [
  {
    question: 'What if I don’t know what to input to get started?',
    answer:
      'The AI tools provide example prompts and suggestions to help you get started. You can also use predefined templates for common tasks like blog writing, email drafts, or transcription.',
  },
  {
    question:
      'Can I trust the content generated by AI to be factually accurate?',
    answer:
      'While the AI generates content based on patterns and data, it’s always a good idea to verify facts, especially for technical or research-heavy topics.',
  },
  {
    question: 'How do I improve the quality of AI-generated content?',
    answer:
      'Providing detailed and specific inputs, such as keywords, context, or examples, can significantly enhance the output quality.',
  },
  {
    question: 'Does the AI Writer support languages other than English?',
    answer:
      'Yes, the tools support multiple languages. Check the supported language list in the tool settings.',
  },
];

export const blogWriterFaqs = [
  {
    question: ' How do I handle writer’s block while using the AI Blog Writer?',
    answer:
      'If you’re unsure about what to input, the tool provides suggestions for blog topics and outlines based on your chosen category or keywords.',
  },
  {
    question: 'How can I make the blog unique and avoid plagiarism?',
    answer:
      'The AI Blog Writer generates original content, but for extra assurance, you can use a plagiarism checker to verify uniqueness. Additionally, you can customize the content further.',
  },
  {
    question:
      'Can the AI generate long-form blogs, or is it limited to short posts?',
    answer:
      'Yes, the AI Blog Writer can create both short and long-form blogs. For long posts, you can outline sections or chapters, and the tool will generate detailed content for each.',
  },
];

export const chatWithDocsFaqs = [
  {
    question:
      'Can I upload a scanned document or image instead of a DOCX file?',
    answer:
      'No, the tool currently supports only editable DOCX files. You can convert scanned documents to editable text using OCR tools before uploading.',
  },
  {
    question: 'How does Chat with Docx handle complex or technical documents?',
    answer:
      'The AI can process technical documents, but for best results, ensure the document is well-organized, and your questions are specific.',
  },
  {
    question: 'Can I use this tool to summarize a lengthy document?',
    answer:
      'Yes, you can ask the AI to summarize the entire document or specific sections, saving you time when working with lengthy texts.',
  },
  {
    question: 'What happens if my document includes tables, charts, or images?',
    answer:
      'The tool currently focuses on text-based content and may not interpret tables, charts, or images accurately.',
  },
];

export const botBuilderFaqs = [
  {
    question: 'What is the Bot Builder?',
    answer:
      'The Bot Builder lets you create AI-powered chatbots for platforms like Telegram, enabling automated interactions with your users.',
  },
  {
    question: 'Do I need coding experience to create a bot?',
    answer:
      'No coding experience is required. The Bot Builder provides a step-by-step interface to help you create and customize your bot.',
  },
  {
    question: 'Can I customize the bot’s responses?',
    answer:
      'Yes, you can define how the bot responds to specific inputs, including greetings, FAQs, and commands.',
  },
  {
    question: 'Does the bot support multiple languages?',
    answer:
      "Yes, the bot can be configured to interact in multiple languages, depending on your audience's needs.",
  },
];

export const emailGeneratorFaqs = [
  {
    question: 'How do I generate an email?',
    answer:
      'You can generate an email by entering the required details and clicking the generate button.',
  },
  {
    question: 'Can I customize the email template?',
    answer:
      'Yes, you can customize the email template by editing the fields provided.',
  },
  {
    question: 'Is there a limit to the number of emails I can generate?',
    answer: 'No, there is no limit to the number of emails you can generate.',
  },
];

export const textToLogoFaqs = [
  {
    question: 'Can I create a logo for my business or personal brand?',
    answer:
      'Absolutely! The Text-to-Logo Generator can create professional logos tailored to your brand name, industry, and style preferences.',
  },
  {
    question: 'What if I don’t like the generated logo?',
    answer:
      'You can regenerate the logo with updated inputs or adjust parameters like font style, color palette, or design elements to create a logo you’re satisfied with.',
  },
  {
    question: 'Does the tool support transparent backgrounds for logos?',
    answer:
      'Yes, logos are generated with transparent backgrounds, making them easy to use across different platforms and media.',
  },
];

export const transcriptGeneratorFaqs = [
  {
    question: 'Does the tool handle multiple languages in audio files?',
    answer:
      'Yes, the tool can process multilingual audio, but the transcription quality may vary depending on the complexity and clarity of the language shifts.',
  },
  {
    question: 'What if I only want to transcribe a specific part of the audio?',
    answer:
      'You can specify the time range (e.g., 10:00 to 15:00) in the input, and the tool will focus on transcribing that section.',
  },
  {
    question:
      'How do I ensure the transcript is formatted properly for my needs?',
    answer:
      'After the transcript is generated, you can customize the formatting (e.g., paragraphs, speaker labels) directly within the tool.',
  },
];

export const textToSpeechFaqs = [
  {
    question: 'What does the Text-to-Speech Generator do?',
    answer:
      'The Text-to-Speech (TTS) Generator converts written text into spoken audio, allowing you to create voiceovers, narrations, or audiobooks.',
  },
  {
    question: 'Can I select different voices and languages?',
    answer:
      'Yes, the tool offers a wide range of voices and languages. You can choose accents, genders, and voice styles to suit your project.',
  },
  {
    question: 'What if the pronunciation of certain words is incorrect?',
    answer:
      'You can adjust the text input using phonetic spellings or specific formatting to correct pronunciations. Some tools also allow custom pronunciation settings.',
  },
  {
    question:
      'Can I generate audio for long-form content like books or guides?',
    answer:
      'Yes, you can use the TTS Generator for long-form content. However, longer texts may need to be split into smaller sections for processing.',
  },
  {
    question: 'How do I ensure the speech pacing matches my needs?',
    answer:
      'The tool allows you to adjust speech speed and pauses, enabling you to control the pacing for clarity and engagement.',
  },
  {
    question: 'Is there a character limit for the text I can input?',
    answer:
      'Yes, some tools have character limits per session. For longer texts, you may need to input content in segments.',
  },
];

export const upscaleImageFaqs = [
  {
    question: 'What does the Upscale Image tool do?',
    answer:
      'The Upscale Image tool enhances the resolution of your images, making them clearer and sharper for use in larger formats without losing quality.',
  },
  {
    question: 'Can the tool upscale small, blurry images effectively?',
    answer:
      'While the tool enhances resolution, the results depend on the original image quality. Extremely blurry or low-quality images may not yield perfect results.',
  },
  {
    question: 'Is there a limit on the size of the image I can upscale?',
    answer:
      'Yes, there may be file size or resolution limits. If your file exceeds these limits, you’ll need to reduce its size before uploading.',
  },
];

export const objectRemoverFaqs = [
  {
    question: 'How does the Object Remover work?',
    answer:
      'The Object Remover allows you to highlight and remove unwanted objects or elements from an image seamlessly, filling the space with surrounding content.',
  },
  {
    question: 'What if the area where the object was removed looks unnatural?',
    answer:
      'You can adjust the selection or try regenerating the fill. For complex backgrounds, minor manual editing may be needed afterward.',
  },
  {
    question: 'Can I use the Object Remover on intricate backgrounds?',
    answer:
      'Yes, the tool works on intricate backgrounds, but for best results, keep your selection precise and use high-resolution images.',
  },
];

export const objectReplacerFaqs = [
  {
    question: 'What is the AI Object Replacer?',
    answer:
      'The AI Object Replacer allows you to replace one object in an image with another, based on your input description or a replacement object you upload.',
  },
  {
    question: 'Can I specify the style of the replacement object?',
    answer:
      'Yes, you can describe the replacement object, including its style, size, and position, to ensure it blends naturally into the image.',
  },
  {
    question: 'What if the replacement doesn’t look realistic?',
    answer:
      'You can refine your input description or make manual adjustments to ensure the replacement looks more natural',
  },
  {
    question: 'Are there limitations on the types of objects I can replace?',
    answer:
      'The tool works best with clearly defined objects. Complex replacements may require detailed input or multiple attempts for optimal results.',
  },
];

export const brandbookFaqs = [
  {
    question: 'What is a BrandBook, and why do I need one?',
    answer:
      'A BrandBook is a visual and style guide for your brand, including logo design, colors, and tone of voice. It helps ensure consistency across all brand communications.',
  },
  {
    question: 'Can I create a complete branding kit with this tool?',
    answer:
      'Yes, the BrandBook Generator provides a comprehensive branding kit, including logo variations, color palettes (with gradients and hex codes), slogan and tone guidelines.',
  },
  {
    question: 'How do I choose the right colors and fonts for my brand?',
    answer:
      'The tool uses AI to recommend colors and fonts based on your industry, brand personality, and target audience. You can also customize the suggestions to suit your preferences.',
  },
  {
    question: 'Can I update the BrandBook after it’s generated?',
    answer:
      'Yes, you can revise the elements of your BrandBook and regenerate it as your brand evolves or if you want to explore new design directions.',
  },
  {
    question:
      'Does the tool suggest social media content based on my BrandBook?',
    answer:
      'Yes, after generating a BrandBook, you can use the integrated button to create social media content tailored to your brand’s identity.',
  },
  {
    question: 'What if I need custom logos or designs?',
    answer:
      'The tool provides text-based logo generation, but for advanced customization, you may need to refine the designs using professional graphic design tools.',
  },
];

export const podcastFaqs = [
  {
    question: 'What is the Podcast Generator?',
    answer:
      'The Podcast Generator allows you to create fully scripted and narrated podcasts based on your input, including topics, styles, and tones.',
  },
  {
    question:
      'Can I include multiple voices or characters in a single podcast?',
    answer:
      'Yes, the tool supports multiple voices for hosts and guests. You can choose from a variety of voices and assign them to different roles in the podcast.',
  },
  {
    question: 'How long can the generated podcast be?',
    answer:
      'You can set the duration of the podcast, typically ranging from a few minutes to an hour, depending on your requirements.',
  },
  {
    question: 'Can I edit the podcast script before generation?',
    answer:
      'Yes, you can review and edit the script generated by the tool before the audio is created, ensuring it aligns with your vision.',
  },
  {
    question: 'What if the podcast sounds robotic or unnatural?',
    answer:
      'The tool uses advanced AI for natural-sounding voices, but you can tweak the tone, pacing, and voice selection to make it more engaging.',
  },
];

export const textToImageFaqs = [
  {
    question: 'What is a text-to-image AI generative tool?',
    answer:
      'A text-to-image AI generative tool creates a brand-new image from a written description you provide. You simply type in a prompt (e.g. “a serene mountain lake at sunrise in watercolor style”) and the AI renders an original illustration or photo-realistic picture matching your text.',
  },
  {
    question: 'Can I control the style, composition, or mood of the generated image?',
    answer:
      'Yes—your prompt is your control panel. You can specify artistic styles (watercolor, oil painting, cyberpunk, etc.), camera angles, lighting conditions, color palettes, emotional tone, and even level of detail to guide the AI toward your vision.',
  },
  {
    question: 'What output formats and resolutions are available?',
    answer:
      'Most tools let you download in common formats like JPG or PNG, often up to 1024×1024 or 2048×2048 px. Some platforms also support higher-res “HD” exports or vector-style SVGs—check your tool’s settings for exact limits.',
  },
  {
    question: 'How long does it take to generate an image?',
    answer:
      ' Generation times typically range from a few seconds to under a minute. However, if many users are submitting prompts simultaneously, you may experience slightly longer wait times due to server load.',
  },

];

export const imageToImageFaqs = [
  {
    question: 'What is KreaitorAI image-to-image tool?',
    answer:
      'A KreaitorAI image-to-image tool transforms an input image into a new version based on specific instructions or styles. This could include turning sketches into realistic photos, applying artistic styles, or generating alternate versions like cartoon or anime styles.',
  },
  {
    question: 'What image formats are supported for upload?',
    answer:
      'We support popular formats like JPG, JPEG &  PNG. Output images are usually available in high-resolution JPG or PNG for easy downloading and sharing.',
  },
  {
    question: 'How long does it take to generate an image?',
    answer:
      'Most image-to-image transformations are completed within a few seconds. However if many users are active at once, it may take a bit longer for your image to process.',
  },

];
export const socialMediaFaqs = [
  {
    question: 'What is the Social Media Generator?',
    answer:
      'The Social Media Generator helps you create engaging social media content, including captions, posts, and hashtags, tailored to your audience and platform.',
  },
  {
    question:
      'Can I generate posts for specific platforms like Instagram or LinkedIn?',
    answer:
      'Yes, the tool is optimized for multiple platforms. You can select the desired platform to generate content that aligns with its tone and style.',
  },
  {
    question: 'How do I ensure the generated content matches my brand’s voice?',
    answer:
      'You can specify your brand’s tone (e.g., professional, casual, playful) and key values in the input to ensure alignment with your voice.',
  },
  {
    question: 'Does the tool generate hashtags and keywords for better reach?',
    answer:
      'Yes, the tool suggests relevant hashtags and keywords based on your content and platform to help improve visibility and engagement.',
  },
  {
    question: 'Can I generate content for trending topics or events?',
    answer:
      'Yes, you can include trending topics, events, or keywords in your input, and the tool will generate relevant posts to match the trends.',
  },
  {
    question: 'What if I need to edit the generated post?',
    answer:
      'You can edit it directly within the tool or using external platforms before publishing.',
  },
];
